import Image from 'next/image';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import clsx from 'clsx';

//hooks
import useDarkMode from '@/utils/hooks/utils/useDarkMode';
import useSubscription from '@/utils/hooks/swr/pricing/useSubscription';
import useReceipts from '@/utils/hooks/swr/pricing/useReceipts';
import useActiveTreeStructure from '@/utils/hooks/tree-structure/useActiveTreeStructure';

//components
import SettingsWrapper from '@/components/layouts/LayoutPlatform/components/SettingsWrapper';
import CompoundBanner from '@/components/base/InfoBanner/CompoundBanner';
import H2 from '@/components/base/Headings/H2';
import Button from '@/components/base/Button';

//images
import errorIllustrationLight from './../../../../../../public/images/pricing/checkout/jellyco-error-light.webp';
import errorIllustrationDark from './../../../../../../public/images/pricing/checkout/jellyco-error-dark.webp';

type ErrorPageContentProps = { className?: string };

const ErrorPageContent = ({ className }: ErrorPageContentProps) => {
    const { mutateSubscription } = useSubscription();
    const { mutateReceipts } = useReceipts();
    const { t } = useTranslation('settings');
    const { isDarkMode } = useDarkMode();
    const errorIllustration = isDarkMode() ? errorIllustrationDark : errorIllustrationLight;

    async function retryRequest() {
        await mutateSubscription(undefined);
        await mutateReceipts(undefined);
    }

    return (
        <div className={clsx('flex flex-1 flex-col space-y-8', className)}>
            <CompoundBanner data-testid="error-banner-subscription">
                <CompoundBanner.Icon icon="warning" />
                <CompoundBanner.Content
                    title={t('errors:error_occured')}
                    message={t('errors:error_occured_fetch_data')}
                />
            </CompoundBanner>
            <div
                className="flex flex-1 flex-col items-center justify-center space-y-8"
                data-testid="error-content">
                <Image
                    src={errorIllustration}
                    alt="error illustration"
                    width={216}
                    height={149}
                    placeholder="blur"
                />
                <div className="mt-2 flex flex-col items-center space-y-3">
                    <H2>Oops</H2>
                    <span className="text-center font-GMedium text-icoGray-500 dark:text-icoDarkMode-wolf">
                        <Trans
                            i18nKey="errors:error_with_you_request"
                            components={{ br: <br /> }}
                        />
                    </span>
                </div>
                <div className="mt-2 flex flex-col items-center space-y-3">
                    <Button size="xl" onClick={retryRequest} data-testid="retry-button">
                        {t('retry')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export function ErrorPage() {
    const { isTreeStructureActivated } = useActiveTreeStructure();

    if (isTreeStructureActivated) {
        return (
            <SettingsWrapper.MainContent>
                <ErrorPageContent />
            </SettingsWrapper.MainContent>
        );
    }

    return <ErrorPageContent className="p-8" />;
}
